import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { HTMLService } from "@services/htmlService.service";
import { fromEvent, Subscription } from "rxjs";
import { distinctUntilChanged, filter } from "rxjs/operators";
import { animDropDown } from 'src/app/animations/anim-dropdown.animations';
import "./v-dropdown.component.scss";


export enum VerticalDirection {
    Down,
    Up
}

@Component({
    selector: 'v-dropdown',
    template: require('./v-dropdown.component.html'),
    animations: [animDropDown]
})
export class VDropdownComponent implements OnInit, OnChanges {

    @Input() open: boolean = false;

    @Input() autoSize: boolean;
    @Input() showMask: boolean;
    @Input() width: number;
    @Input() height: number;

    @Input() closeOnEsc: boolean = true;
    @Input() closeOnClickOutside: boolean = true;
    @Input() closeOnClickInside: boolean = true;

    @Input() direction = VerticalDirection.Down;

    @Input() ignoreClicksFrom: ElementRef<HTMLElement>;

    // Todo: naming
    @Output() closed = new EventEmitter<MouseEvent>();
    @Output() opened = new EventEmitter<void>();

    // ---

    dropdownHeight = 1000;

    private showDropDown = false;
    private readonly maxDropDownHeight = 500;
    private readonly minDropDownHeight = 200;

    private subscriptions = new Subscription();
    private outsideClickSubscriptions: Subscription;

    constructor(
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly htmlService: HTMLService) {
    }

    ngOnInit(): void {
        this.subscribeToEvents();
    }

    ngOnDestroy(): void {
        this.subscriptions?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges) {

        if (changes["open"]) {
            // Reset this so we dont influence the next opening
            this.dropdownHeight = 1000;

            setTimeout(() => {
                var previousState = this.showDropDown;
                this.showDropDown = this.open;
                this.calculateStyles();

                // If we were open, and now closed, then ensure we emit
                if (!this.showDropDown && previousState) {
                    this.closed.emit();
                }
            });

            if (this.open) {
                setTimeout(() => {
                    this.subscribeToOutsideClicks();
                }, 10);
            }
            // Only unsubscribe if we have been open previously
            else if (changes["open"].previousValue == true) {
                this.unsubcribeToOutsideClicks();
            }
        }
    }

    // ---

    private subscribeToOutsideClicks() {

        this.outsideClickSubscriptions = fromEvent(document.body, 'click',
            {
                capture: true,
            })
            .subscribe((event: MouseEvent) => {
                this.handleClick(event);
            });
    }

    private unsubcribeToOutsideClicks() {
        if (!this.outsideClickSubscriptions) {
            return;
        }

        this.outsideClickSubscriptions.unsubscribe();
        this.outsideClickSubscriptions = null;
    }

    private subscribeToEvents(): void {

        this.subscriptions.add(fromEvent(document, "keydown").pipe(
            filter((event: KeyboardEvent) => event.keyCode === 27),
            distinctUntilChanged()
        ).subscribe((event: KeyboardEvent) => this.handleEscPressed(event)));
    }

    private handleEscPressed(event: KeyboardEvent): void {

    }

    private hideDropDown(event: MouseEvent) {
        this.unsubcribeToOutsideClicks();
        this.showDropDown = false;
        this.closed.emit(event);
    }

    // Returns true if the click is allowed to be handled by another component (e.g. to click a link)
    // Returns false if the click should be supressed
    private handleClick(event: MouseEvent): boolean {

        if (!this.elementRef.nativeElement || !this.showDropDown) {
            return;
        }

        const nodeTarget = event.target as HTMLElement;

        const clickedContent = !nodeTarget.classList.contains("mask") && this.elementRef.nativeElement?.contains(nodeTarget);
        const clickedIgnoreElement = this.ignoreClicksFrom?.nativeElement?.contains(nodeTarget);

        if (!clickedContent && !clickedIgnoreElement) {
            if (this.closeOnClickOutside) {
                this.hideDropDown(event);
            }
        }

        if (!nodeTarget.classList.contains("mask")) {
            return true;
        }
    }

    private calculateStyles() {
        this.findContentsHeight();
        this.direction = VerticalDirection.Down;

        if (!this.showDropDown) {
            return;
        }

        if (this.height) {
            this.dropdownHeight = this.height;
            return;
        }

        var dropdownElementBox = this.elementRef.nativeElement?.getBoundingClientRect();
        var scrollParent = this.htmlService.getScrollParent(this.elementRef.nativeElement, true);

        var minPercentage = 100 - (this.minDropDownHeight * 100 / scrollParent.scrollHeight);

        // If our element is too close to the bottom of our container, then open upwards (if there is space)
        var positionPercentage = dropdownElementBox.top * 100 / scrollParent.scrollHeight;
        var scaleFactor = 1;
        this.dropdownHeight = Math.min(this.maxDropDownHeight, (100 - positionPercentage) * scaleFactor * scrollParent.scrollHeight / 100);

        var heightBuffer = 20;

        if (positionPercentage > minPercentage && (scrollParent.getBoundingClientRect().top > this.dropdownHeight ||
            scrollParent.getBoundingClientRect().bottom > this.dropdownHeight)) {
            this.direction = VerticalDirection.Up;
            this.dropdownHeight = Math.min(this.maxDropDownHeight, positionPercentage * scrollParent.scrollHeight / 100);
        }

        this.dropdownHeight -= heightBuffer;
    }

    private findContentsHeight() {
        if (!this.showDropDown || !this.autoSize) {
            return;
        }

        var contentsContainer: HTMLElement = this.elementRef.nativeElement?.querySelector(".dropdown-content-container");
        var dropdownContents: HTMLElement;

        contentsContainer?.childNodes.forEach(node => {
            if (!dropdownContents && node.nodeType != 8) {
                dropdownContents = node as HTMLElement;
            }
        });

        if (!dropdownContents) {
            setTimeout(() => this.findContentsHeight());
            return;
        }

        var rect = dropdownContents.getBoundingClientRect();
        var targetHeight = Math.min(this.maxDropDownHeight, rect.height);
        targetHeight = Math.max(this.minDropDownHeight, targetHeight);

        contentsContainer.style.minHeight = targetHeight + "px";
        contentsContainer.style.width = rect.width + "px";
    }
}
